import { useReportingStore } from "../../../../../stores/ReportingStore";
import LoaderStats from "../../LoaderStats";
import {
  ColorSwatch,
  Group,
  NumberFormatter,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { DonutChart } from "@mantine/charts";
import { useMemo, useState } from "react";
import CustomActiveShape from "./CustomActiveShape";
import ReportingInfo from "../../ReportingInfo";
import LinkGrabber from "../../LinkGrabber";
import { useLanguage } from "../../../../../stores/LanguageStore";

const ResponseQuality = () => {
  const lang = useLanguage((s) => s.language);
  const dateRange = useReportingStore((s) => s.dateRange);
  const actualStats = useReportingStore((s) => s.actualStats);
  const actualStatsDate = useReportingStore((s) => s.actualStatsDate);

  const [hoveredIndex, setHoveredIndex] = useState(0);

  const handleHover = (_, index) => {
    setHoveredIndex(index);
  };

  const statistics = useMemo(() => {
    if (!dateRange.start_date && !dateRange.end_date) {
      return actualStats;
    } else {
      const totals = actualStatsDate.reduce((acc, curr) => {
        Object.keys(curr).forEach((key) => {
          if (key !== "date") {
            acc[key] = (acc[key] || 0) + curr[key];
          }
        });
        return acc;
      }, {});

      return totals;
    }
  }, [actualStatsDate, dateRange.end_date, dateRange.start_date, actualStats]);

  const positiveRate = useMemo(() => {
    if (statistics.email_answer) {
      return (
        ((statistics?.email_answer_positive || 0) / statistics.email_answer) *
        100
      );
    }
    return 0;
  }, [statistics.email_answer, statistics.email_answer_positive]);

  const negativeRate = useMemo(() => {
    if (statistics.email_answer) {
      return (
        ((statistics.email_answer_negative || 0) / statistics.email_answer) *
        100
      );
    }
    return 0;
  }, [statistics.email_answer, statistics.email_answer_negative]);

  const neutralRate = useMemo(() => {
    if (statistics.email_answer) {
      return (
        ((statistics.email_answer_neutral || 0) / statistics.email_answer) * 100
      );
    }
    return 0;
  }, [statistics.email_answer, statistics.email_answer_neutral]);

  const data = useMemo(
    () => [
      {
        name: lang.reports.others,
        value: Math.round(neutralRate * 10) / 10,
        full: statistics.email_answer_neutral,
        color: "gray.5",
      },
      {
        name: lang.statistics.contacts_type.postitive,
        value: Math.round(positiveRate * 10) / 10,
        full: statistics.email_answer_positive,
        color: "green",
      },
      {
        name: lang.statistics.contacts_type.negative,
        value: Math.round(negativeRate * 10) / 10,
        full: statistics.email_answer_negative,
        color: "red",
      },
    ],
    [
      negativeRate,
      neutralRate,
      positiveRate,
      statistics.email_answer_negative,
      statistics.email_answer_neutral,
      statistics.email_answer_positive,
      lang,
    ]
  );

  return (
    <Paper
      pos={"relative"}
      flex={1}
      p={"xl"}
      pt={"md"}
      component={Stack}
      align="center"
    >
      <LoaderStats isDate />

      <Group mb={16} justify="center">
        <ReportingInfo>
          <Text>{lang.reports.answers_title}</Text>
        </ReportingInfo>

        <Text size="lg" fw={600}>
          {lang.reports.answers_quality}{" "}
          <span style={{ color: "black" }}>
            ({" "}
            <b>
              <NumberFormatter value={statistics.email_answer} />
            </b>{" "}
            )
          </span>
        </Text>

        <LinkGrabber />
      </Group>

      <Group>
        {statistics.email_answer > 0 ? (
          <DonutChart
            w={"1100%"}
            h={250}
            data={data}
            thickness={32}
            withTooltip={false}
            paddingAngle={3}
            pieProps={{
              isAnimationActive: true,
              animationDuration: 600,
              activeShape: (props) => <CustomActiveShape {...props} />,
              activeIndex: hoveredIndex,
              onMouseEnter: handleHover,
            }}
          />
        ) : (
          <Text w={"100%"} ta={"center"} h={100} mt={64}>
            {lang.reports.no_data}
          </Text>
        )}

        <Group gap={"md"} justify="center" w={"100%"}>
          <Group gap={4}>
            <ColorSwatch color={`var(--mantine-color-gray-5)`} size={16} />
            <Text size="sm">{lang.reports.others}</Text>
          </Group>
          <Group gap={4}>
            <ColorSwatch color={`var(--mantine-color-green-5)`} size={16} />
            <Text size="sm">{lang.statistics.contacts_type.postitive}</Text>
          </Group>
          <Group gap={4}>
            <ColorSwatch color={`var(--mantine-color-red-5)`} size={16} />
            <Text size="sm">{lang.statistics.contacts_type.negative}</Text>
          </Group>
        </Group>
      </Group>
    </Paper>
  );
};

export default ResponseQuality;
